// Reporte de derivaciones (Centro Médico → Ocupacional). Lista las atenciones
// clasificadas por médico derivador / convenio, filtrable por rango de fecha.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, GitBranch, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Convenio, DerivacionRow, MedicoRow, fieldCls, medRpc } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const hoy = () => fechaLocal();
const inicioMes = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

export function ReporteDerivaciones() {
  const [filtro, setFiltro] = useState({ desde: inicioMes(), hasta: hoy(), derivador: '', convenio: '' });
  const [aplicado, setAplicado] = useState(filtro);
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  const { data: convenios = [] } = useQuery<Convenio[]>({ queryKey: ['med-convenios', 'todos'], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: null }) });

  const { data: rows = [], isFetching } = useQuery<DerivacionRow[]>({
    queryKey: ['med-derivaciones', aplicado],
    queryFn: () => medRpc<DerivacionRow[]>('med_reporte_derivaciones', {
      _desde: aplicado.desde || null, _hasta: aplicado.hasta || null,
      _medico_derivador: aplicado.derivador || null, _convenio: aplicado.convenio || null,
    }),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><GitBranch className="w-5 h-5" /> Reporte de derivaciones</h1>
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1"><span className="text-xs font-medium text-muted-foreground">Desde</span><input type="date" className={fieldCls} value={filtro.desde} onChange={(e) => setFiltro({ ...filtro, desde: e.target.value })} /></label>
            <label className="space-y-1"><span className="text-xs font-medium text-muted-foreground">Hasta</span><input type="date" className={fieldCls} value={filtro.hasta} onChange={(e) => setFiltro({ ...filtro, hasta: e.target.value })} /></label>
            <label className="space-y-1"><span className="text-xs font-medium text-muted-foreground">Médico derivador</span>
              <select className={`${fieldCls} w-56`} value={filtro.derivador} onChange={(e) => setFiltro({ ...filtro, derivador: e.target.value })}>
                <option value="">Todos</option>{medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
              </select>
            </label>
            <label className="space-y-1"><span className="text-xs font-medium text-muted-foreground">Convenio</span>
              <select className={`${fieldCls} w-56`} value={filtro.convenio} onChange={(e) => setFiltro({ ...filtro, convenio: e.target.value })}>
                <option value="">Todos</option>{convenios.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
              </select>
            </label>
            <Button size="sm" onClick={() => setAplicado(filtro)} isLoading={isFetching}>{!isFetching && <Filter className="w-4 h-4" />} Filtrar</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Derivaciones ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Paciente</th><th className="py-2 pr-3">Atendió</th>
              <th className="py-2 pr-3">Médico derivador</th><th className="py-2 pr-3">Convenio</th>
              <th className="py-2 pr-3 text-right">Base</th><th className="py-2 pr-3 text-right">Comisión %</th><th className="py-2 pr-3 text-right">Comisión $</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin derivaciones en el rango</td></tr>}
              {rows.map((d) => (
                <tr key={d.evolucion_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{new Date(d.fecha).toLocaleDateString(LOCALE)}</td>
                  <td className="py-2 pr-3">{d.paciente}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{d.medico ?? '—'}</td>
                  <td className="py-2 pr-3">{d.derivador ?? '—'}</td>
                  <td className="py-2 pr-3">{d.convenio ?? '—'}{d.convenio_tipo ? <span className="ml-1 text-xs text-muted-foreground">({d.convenio_tipo})</span> : ''}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">${Number(d.base).toFixed(2)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{d.comision_pct != null ? `${Number(d.comision_pct)}%` : '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">${Number(d.monto_comision).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="border-t font-semibold">
                <td className="py-2 pr-3" colSpan={7}>Total comisiones</td>
                <td className="py-2 pr-3 text-right tabular-nums">${rows.reduce((s, d) => s + Number(d.monto_comision || 0), 0).toFixed(2)}</td>
              </tr></tfoot>
            )}
          </table>
          <p className="mt-2 text-xs text-muted-foreground">Base = precio de consulta del médico tratante. Comisión = base × % del convenio.</p>
        </CardContent>
      </Card>
    </div>
  );
}
